import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSiteAppearance } from '../hooks/useSiteAppearance';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { CONTENT_KEYS, SITE_FOOTER_DEFAULT, SITE_LABELS_DEFAULT, type PageVisibilityContent, type SiteFooterContent, type SiteLabelsContent } from '../content/schema';
import './site.css';

const NAV: { to: string; label: keyof SiteLabelsContent; page?: keyof PageVisibilityContent }[] = [
  { to: '/about', label: 'about', page: 'about' },
  { to: '/services', label: 'services', page: 'services' },
  { to: '/work', label: 'work', page: 'work' },
  { to: '/projects', label: 'projects', page: 'projects' },
  { to: '/media', label: 'media', page: 'blogIndex' },
  { to: '/contact', label: 'contact', page: 'contact' },
];

const LINK_PAGES: [string, keyof PageVisibilityContent][] = [
  ['/services', 'services'], ['/work', 'work'], ['/about', 'about'], ['/contact', 'contact'],
  ['/ai-readiness', 'aiReadiness'], ['/post', 'postIndex'], ['/blog/insights', 'blogIndex'],
];

const pageFor = (to: string) => LINK_PAGES.find(([prefix]) => to === prefix || to.startsWith(`${prefix}?`) || to.startsWith(`${prefix}/`))?.[1];

/** The frame for public pages outside the world: blog, legal, sign-in, not found. */
export function SiteShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  useSiteAppearance();
  const { isVisible } = usePageVisibility();
  const { data: footer } = useContent<SiteFooterContent>(CONTENT_KEYS.siteFooter, SITE_FOOTER_DEFAULT);
  const { data: labelEdits } = useContent<Partial<SiteLabelsContent>>(CONTENT_KEYS.siteLabels, {});
  const labels = { ...SITE_LABELS_DEFAULT, ...Object.fromEntries(Object.entries(labelEdits).filter(([, value]) => value)) };
  return (
    <div className="sfp">
      <a className="sfp-skip" href="#sfp-main">Skip to content</a>
      <header className="sfp-head">
        <Link className="sfp-wordmark" to="/" aria-label="subfocus.ai home">subfocus.ai</Link>
        <nav aria-label="Main navigation">
          {NAV.filter(({ page }) => !page || isVisible(page)).map(({ to, label }) => (
            <Link key={to} to={to} className={pathname.startsWith(to) ? 'is-active' : ''} aria-current={pathname.startsWith(to) ? 'page' : undefined}>{labels[label]}</Link>
          ))}
        </nav>
        <Link className="sfp-back" to="/">Back to the world <span>↗</span></Link>
      </header>
      <main id="sfp-main" className="sfp-main">{children}</main>
      <footer className="sfp-foot">
        <div className="sfp-foot-brand">
          <Link className="sfp-wordmark" to="/">subfocus.ai</Link>
          <p>{footer.tagline}<br />{footer.address}<br /><a href={`mailto:${footer.email}`}>{footer.email}</a></p>
        </div>
        {footer.columns.map((column) => {
          const links = column.links.filter((link) => {
            const page = pageFor(link.to);
            return !page || isVisible(page);
          });
          if (!links.length) return null;
          return (
            <div key={column.heading}>
              <h2>{column.heading}</h2>
              {links.map((link) => (link.to.startsWith('http')
                ? <a key={link.to} href={link.to} target="_blank" rel="noreferrer">{link.label}</a>
                : <Link key={link.to} to={link.to}>{link.label}</Link>))}
            </div>
          );
        })}
      </footer>
    </div>
  );
}
